import { useEffect, useRef, useState } from 'react';

import { Location, useBlocker } from 'react-router-dom';

type Props = {
  askBeforeLeaving?: boolean | ((props: { nextLocation: Location }) => boolean);
  guard: (onAllow: () => void, onDecline: () => void) => void;
};

export const RouteLeaveGuard = ({ guard, askBeforeLeaving }: Props) => {
  const guardRef = useRef<boolean>(false);
  const [locationToProtect, setLocationToProtect] = useState<Location | null>(
    null
  );

  const blocker = useBlocker(({ nextLocation }) => {
    if (locationToProtect) {
      return true;
    }

    if (
      typeof askBeforeLeaving === 'function' &&
      askBeforeLeaving({ nextLocation })
    ) {
      setLocationToProtect(nextLocation);
      return true;
    }

    if (typeof askBeforeLeaving == 'boolean' && askBeforeLeaving) {
      setLocationToProtect(nextLocation);
      return true;
    }

    return false;
  });

  useEffect(() => {
    if (guard && blocker.state === 'blocked') {
      if (!guardRef.current) {
        guardRef.current = true;
        guard(
          () => {
            setLocationToProtect(null);
            if (blocker.state === 'blocked') {
              blocker.proceed(); // proceed to the blocked location
            }
            guardRef.current = false;
          },
          () => {
            setLocationToProtect(null);
            if (blocker.state === 'blocked') {
              blocker.reset(); // back to an unblocked state and leave the user at the current location
            }
            guardRef.current = false;
          }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guard, blocker.state, blocker.proceed]);

  return null;
};
